import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { Upload, IconFont, FileItem, FilePreview } from '~/components';
import { IFile, NewUploadFilesValue, UploadProgress } from '~/interfaces';
import { findIndex, get, isEmpty, isFunction, post, first } from '~/utils';
import Intl from '@fx-ui/intl';
import { UploadFile } from '@fx-ui/fine-design';
import { useStores } from '~/hooks';
import { EMPTY_FN } from '~/constants';

interface IFileUploadProps {
    className?: string;
    action?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    value?: IFile[];
    accept?: string;
    enableDrag?: boolean;
    maxFileSize?: number;
    maxSizeTip?: string;
    maxFileCount?: number;
    maxCountTip?: string;
    multiple?: boolean;
    uploadTokenUrl?: string;
    uploadPolicyUrl?: string;
    onChange?: (value: IFile[]) => void;
    onStateChange?: (loading: boolean) => void;
    onRenderUploadBtn?: () => React.ReactNode;
    onGetUploadPolicyResult?: (fileList: FileList) => object;
    onGetUploadPolicyError?: (error: object) => void;
    onBeforeUpload?: (fileList: FileList) => boolean | Promise<any>;
    onProgress?: (event: UploadProgress, file: UploadFile) => void;
    onError?: (err: any, file: UploadFile) => void;
    onSuccess?: (body: any, file: UploadFile) => void;
    hasPreview?: boolean;
    onRemove?: (_id: string | null) => void;
    newUploadFileProps?: { maxWidth?: number; maxHeight?: number };
}

const FileUpload: React.FunctionComponent<IFileUploadProps> = (props) => {
    const {
        className,
        action = '',
        disabled,
        style,
        value,
        accept,
        multiple = false,
        enableDrag = true,
        maxFileSize = 5 * 1024 * 1024,
        maxSizeTip,
        maxFileCount,
        maxCountTip,
        uploadTokenUrl,
        uploadPolicyUrl,
        onStateChange,
        onChange,
        onBeforeUpload,
        onProgress,
        onError,
        onSuccess,
        onRenderUploadBtn,
        onGetUploadPolicyError,
        hasPreview = true,
        onRemove = EMPTY_FN,
        newUploadFileProps
    } = props;

    const [dragging, setDragging] = useState<boolean>(false);
    const [previewFile, setPreviewFile] = useState<IFile[]>(value || []);
    const [previewVisible, setPreviewVisible] = useState<boolean>(false);
    const [previewIndex, setPreviewIndex] = useState<number>(0);

    const uploadRef = useRef<any>();

    const { corpUserStore } = useStores();

    const renderFilePreview = () => {
        if (!hasPreview) {
            return;
        }
        if (isEmpty(previewFile)) {
            return;
        }
        return (
            <FilePreview
                className="fx-upload-file-preview"
                visible={previewVisible}
                files={previewFile}
                activeIndex={previewIndex}
                onClose={() => setPreviewVisible(false)}
                onSwitchFile={(index) => {
                    setPreviewIndex(index);
                }}
            />
        );
    };

    const renderDefaultFile = (file: IFile, remove: (file: IFile) => void) => {
        return (
            <FileItem
                withLocalPreview={false}
                file={file}
                onDelete={() => {
                    remove(file);
                    onRemove(file._id);
                }}
                onClick={() => {
                    const index = findIndex(
                        previewFile,
                        (currentFile) => currentFile._id === file._id
                    );
                    setPreviewIndex(index);
                    setPreviewVisible(true);
                }}
            />
        );
    };

    const renderNewUploadFile = (newUpload: NewUploadFilesValue, remove: (file: File) => void) => {
        const { _id, finish } = newUpload;
        return (
            <FileItem
                withLocalPreview
                file={newUpload.upFile}
                finish={newUpload.finish}
                progress={newUpload.percent}
                onDelete={() => {
                    remove(newUpload.upFile);
                    onRemove(_id);
                }}
                onClick={() => {
                    if (finish) {
                        const index = findIndex(
                            previewFile,
                            (currentFile) => currentFile._id === _id
                        );
                        setPreviewIndex(index);
                        setPreviewVisible(true);
                    }
                }}
                {...newUploadFileProps}
            />
        );
    };

    const dealUploadChange = (value: IFile[]) => {
        setPreviewIndex(0);
        setPreviewFile(value);
        if (onChange) {
            onChange(value);
        }
    };

    const getOSSUploadData = () => {
        return post(uploadPolicyUrl!, { fileCount: 1 })
            .then((response) => {
                if (uploadRef.current) {
                    uploadRef.current.setActionUrl(get(response, 'data.url'));
                }
                const tokenList = get(response, 'data.tokenList');
                const token = get(response, 'data.token');
                return {
                    token: first(tokenList) || token,
                    'x:uploader': corpUserStore.memberId
                };
            })
            .catch((error) => {
                if (isFunction(onGetUploadPolicyError)) {
                    onGetUploadPolicyError(error);
                }
                throw error;
            });
    };

    return (
        <div className="fx-file-upload" style={style}>
            <Upload
                ref={uploadRef}
                className={classNames('upload-wrapper', className)}
                value={value}
                disabled={disabled}
                enableDrag={enableDrag}
                accept={accept}
                action={action}
                data={uploadPolicyUrl ? getOSSUploadData : undefined}
                uploadTokenUrl={uploadTokenUrl}
                uploadPolicyUrl={uploadPolicyUrl}
                maxFileSize={maxFileSize}
                maxFileCount={maxFileCount}
                maxCountTip={maxCountTip}
                multiple={multiple}
                onBeforeUpload={onBeforeUpload}
                unSupportFileTypeTip={Intl.get('upload.notSupportFileTip')}
                maxSizeTip={maxSizeTip}
                defaultFileRender={renderDefaultFile}
                newUploadFileRender={renderNewUploadFile}
                uploadFailTip={Intl.get('upload.fail.tip')}
                onProgress={onProgress}
                onSuccess={onSuccess}
                onError={onError}
                onChange={dealUploadChange}
                onStateChange={onStateChange}
                onDrop={() => setDragging(false)}
                onDragEnter={() => setDragging(true)}
                onDragLeave={() => setDragging(false)}
            >
                <div
                    className={classNames('upload-btn', {
                        dragging: dragging
                    })}
                >
                    {onRenderUploadBtn ? (
                        onRenderUploadBtn()
                    ) : (
                        <>
                            <IconFont className="upload-icon" type="file-upload" />
                            {Intl.get('file.select.detail', { num: maxFileSize / (1024 * 1024) })}
                        </>
                    )}
                </div>
            </Upload>
            {renderFilePreview()}
        </div>
    );
};

export default FileUpload;
